import React from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUI } from '../contexts/UIContext';
import { patientsService } from '../adapters/patients';
import { Patient } from '../types';

const MIN_LEN = 3;
const RESULT_LIMIT = 10;

export const GlobalPatientSearch = () => {
	const [term, setTerm] = React.useState('');
	const [loading, setLoading] = React.useState(false);
	const [results, setResults] = React.useState<Patient[]>([]);
	const [open, setOpen] = React.useState(false);
	const [activeIdx, setActiveIdx] = React.useState(-1);
	const navigate = useNavigate();
	const location = useLocation();
	const containerRef = React.useRef<HTMLDivElement>(null);
	const requestIdRef = React.useRef(0);
	const { primaryOpen } = useUI();

	React.useEffect(() => { setOpen(false); setActiveIdx(-1); }, [location.pathname]);

	React.useEffect(() => {
		if (primaryOpen) { setOpen(false); setResults([]); setTerm(''); setActiveIdx(-1); }
	}, [primaryOpen]);

	const runSearch = React.useCallback(async (q: string) => {
		if (q.length < MIN_LEN) { setResults([]); setOpen(false); return; }
		const myId = ++requestIdRef.current;
		setLoading(true);
		try {
			const data = await patientsService.searchPatients(q, RESULT_LIMIT, null);
			if (requestIdRef.current !== myId) return;
			setResults(data.items || []);
			setOpen(true);
			setActiveIdx(-1);
		} catch (_) {}
		finally {
			if (requestIdRef.current === myId) setLoading(false);
		}
	}, []);

	React.useEffect(() => {
		if (!term) { setResults([]); setOpen(false); return; }
		const h = setTimeout(() => runSearch(term), 300);
		return () => clearTimeout(h);
	}, [term, runSearch]);

	React.useEffect(() => {
		const onDoc = (e: MouseEvent) => {
			if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
				setOpen(false);
			}
		};
		document.addEventListener('mousedown', onDoc);
		return () => document.removeEventListener('mousedown', onDoc);
	}, []);

	const onSelect = (p: Patient) => {
		navigate(`/patients/${p.patient_id}`);
		setOpen(false);
		setTerm('');
	};

	const onKeyDown = (e: React.KeyboardEvent) => {
		if (!open) return;
		if (e.key === 'ArrowDown') {
			e.preventDefault();
			setActiveIdx(i => (i + 1) % results.length);
		} else if (e.key === 'ArrowUp') {
			e.preventDefault();
			setActiveIdx(i => (i <= 0 ? results.length - 1 : i - 1));
		} else if (e.key === 'Enter') {
			if (activeIdx >= 0 && activeIdx < results.length) {
				e.preventDefault();
				onSelect(results[activeIdx]);
			}
		} else if (e.key === 'Escape') {
			setOpen(false); setActiveIdx(-1); setTerm('');
		}
	};

	const highlight = (namePart: string) => {
		const q = term.toLowerCase();
		if (!q || !namePart.toLowerCase().startsWith(q)) return namePart;
		return (
			<><span className='bg-yellow-200/70 rounded px-0.5'>{namePart.slice(0, q.length)}</span>{namePart.slice(q.length)}</>
		);
	};

	if (primaryOpen) return null;

	return (
		<div ref={containerRef} className='relative' style={{ width: 240 }}>
			<div className='relative'>
				<MagnifyingGlassIcon className='h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none' />
				<input
					type='text'
					value={term}
					onChange={e => setTerm(e.target.value)}
					onFocus={() => { if (results.length > 0) setOpen(true); }}
					onKeyDown={onKeyDown}
					placeholder='Search patients'
					aria-label='Global patient search'
					role='combobox'
					aria-expanded={open}
					aria-autocomplete='list'
					aria-controls='global-patient-search-list'
					aria-activedescendant={activeIdx >= 0 ? `gps-opt-${activeIdx}` : undefined}
					className='w-full pl-8 pr-3 py-2 text-sm rounded-full border border-gray-300 bg-white focus:ring-2 focus:ring-brand-accent focus:border-brand-accent placeholder:text-gray-400'
				/>
			</div>
			{open && results.length > 0 && (
				<ul
					id='global-patient-search-list'
					role='listbox'
					className='absolute mt-1 w-full z-50 bg-white border border-brand-navy/15 shadow-lg rounded-md max-h-80 overflow-auto py-1 text-sm'
				>
					{results.map((p, i) => (
						<li
							id={`gps-opt-${i}`}
							key={p.patient_id}
							role='option'
							aria-selected={i === activeIdx}
							onMouseDown={e => { e.preventDefault(); onSelect(p); }}
							onMouseEnter={() => setActiveIdx(i)}
							className={['px-3 py-2 cursor-pointer flex flex-col gap-0.5', i === activeIdx ? 'bg-brand-accent/10' : 'hover:bg-gray-50'].join(' ')}
						>
							<div className='font-medium text-gray-800 truncate'>
								{highlight(p.first_name)} {highlight(p.last_name)}
							</div>
						</li>
					))}
				</ul>
			)}
			{open && !loading && results.length === 0 && term.length >= MIN_LEN && (
				<div className='absolute mt-1 w-full z-50 bg-white border border-gray-200 shadow rounded-md p-3 text-xs text-gray-500'>No matches</div>
			)}
			{loading && term.length >= MIN_LEN && (
				<div className='absolute mt-1 w-full z-50 bg-white border border-gray-200 shadow rounded-md p-3 text-xs text-gray-500' aria-live='polite'>Searching...</div>
			)}
		</div>
	);
};
